import { createContext, useContext, useState } from 'react'
import { useEphemeralState } from '../../lib/persist.js'

/**
 * ONE employee filter for the roster → payroll tabs of the พนักงาน section.
 *
 * Pick a person on รายชื่อ, ลงเวลา, การลา, หักประจำ or เงินเดือน and the others open
 * already narrowed to that same person. The question HR brings
 * here is usually about one person ("why is this payslip short?"), and answering it
 * means walking ลงเวลา → การลา → เงินเดือน. Until 2026-10-30 each tab kept its own
 * key (emp:ci:emp, emp:lv:emp, emp:sal:emp…) and the person had to be found again on
 * every one of them.
 *
 * Only the PERSON is shared. Branch, period, status and the rest stay per tab — they
 * mean different things on different pages.
 *
 * ประเมิน and เอกสาร are NOT linked (Ram, 2026-10-31): each keeps its own person —
 * `useEmployeeFilter('evaluation')`, `useEmployeeFilter('docs')` (one filter across
 * หนังสือเตือน · ลาออก · ใบรับรอง). They are not part of the monthly run, and arriving
 * there narrowed to the person last looked up on payroll hid everyone else's
 * paperwork.
 *
 * The value is a STRING employee id, '' = everyone — EmployeePicker's contract, and
 * every tab renders its filter with EmployeePicker so the control looks the same
 * wherever the filter follows you.
 *
 * Ephemeral, not persisted: it survives switching tabs and leaving the section, but a
 * hard reload resets it — nobody opens the payroll ledger tomorrow silently narrowed
 * to the one person they looked up today. (Same reasoning as the ประเมิน filters.)
 */
const Ctx = createContext(null)

export function EmployeeFilterProvider({ children }) {
  const state = useEphemeralState('emp:focus', '')
  return <Ctx.Provider value={state}>{children}</Ctx.Provider>
}

/**
 * `[employeeId, setEmployeeId]`. No scope → the shared filter (plain local state
 * outside the provider). A scope → that scope's own person, ephemeral the same way.
 */
export function useEmployeeFilter(scope) {
  const shared = useContext(Ctx)
  const local = useState('')
  const scoped = useEphemeralState(`emp:focus:${scope || '_'}`, '')
  if (scope) return scoped
  return shared || local
}
